// CANONICAL AFFORDANCE CATALOG — single source of truth for interactive props.
//
// One list, shared by BOTH sides (server re-exports via server/src/affordances.ts):
//   • the client renders each room's props from `affordancesIn(id)` (campusMaps.ts),
//   • the server applies the mood effects on `use_object` (IntentResolver) and routes
//     needy agents to the best prop (needs policy).
// Dependency-free (only protocol types) so the server can import it with zero alias config.

import type { LocationId, MoodKey, Tile } from '../net/protocol'

export type Affordance = {
    /** stable id, e.g. 'cantine.self-1' (what use_object targets) */
    id: string
    location: LocationId
    tile: Tile
    label: string
    /** mood deltas applied when used (same numbers the human player gets) */
    effects: Partial<Record<MoodKey, number>>
    /** min sim-minutes between two uses by the same agent (anti-spam; the mood itself self-limits too) */
    cooldownSimMin?: number
}

export const AFFORDANCES: Affordance[] = [
    // infirmerie — repos
    { id: 'infirmerie.lit-1', location: 'infirmerie', tile: { x: 1, y: 2 }, label: 'Se reposer', effects: { energie: 35 }, cooldownSimMin: 30 },
    { id: 'infirmerie.lit-2', location: 'infirmerie', tile: { x: 1, y: 4 }, label: 'Se reposer', effects: { energie: 35 }, cooldownSimMin: 30 },
    // cantine — manger
    { id: 'cantine.self-1', location: 'cantine', tile: { x: 5, y: 3 }, label: 'Manger au self', effects: { faim: 60, fun: 5, energie: 18 }, cooldownSimMin: 60 },
    { id: 'cantine.self-2', location: 'cantine', tile: { x: 15, y: 3 }, label: 'Manger au self', effects: { faim: 60, fun: 5, energie: 18 }, cooldownSimMin: 60 },
    // classroom — réviser (studious only)
    { id: 'classroom.desk-1', location: 'classroom', tile: { x: 8, y: 3 }, label: 'Réviser', effects: { notes: 12, fun: -6, energie: -5 }, cooldownSimMin: 20 },
    { id: 'classroom.desk-2', location: 'classroom', tile: { x: 8, y: 7 }, label: 'Réviser', effects: { notes: 12, fun: -6, energie: -5 }, cooldownSimMin: 20 },
    // cdi — lire / rechercher
    { id: 'cdi.lire-1', location: 'cdi', tile: { x: 9, y: 5 }, label: 'Lire', effects: { fun: 10, notes: 6, energie: -3 }, cooldownSimMin: 20 },
    { id: 'cdi.lire-2', location: 'cdi', tile: { x: 9, y: 8 }, label: 'Lire', effects: { fun: 10, notes: 6, energie: -3 }, cooldownSimMin: 20 },
    { id: 'cdi.recherches', location: 'cdi', tile: { x: 15, y: 3 }, label: 'Recherches', effects: { notes: 10, energie: -5 }, cooldownSimMin: 20 },
    // gymnase — sport (social + fun)
    { id: 'gymnase.sport-1', location: 'gymnase', tile: { x: 1, y: 7 }, label: "S'entraîner", effects: { fun: 10, social: 8, energie: -10 }, cooldownSimMin: 20 },
    { id: 'gymnase.sport-2', location: 'gymnase', tile: { x: 20, y: 7 }, label: "S'entraîner", effects: { fun: 10, social: 8, energie: -10 }, cooldownSimMin: 20 },
    // salle des profs — détente (staff)
    { id: 'salle-profs.detente-1', location: 'salle-profs', tile: { x: 1, y: 2 }, label: 'Se détendre', effects: { social: 8, fun: 6, energie: 5 }, cooldownSimMin: 20 },
    { id: 'salle-profs.detente-2', location: 'salle-profs', tile: { x: 2, y: 2 }, label: 'Se détendre', effects: { social: 8, fun: 6, energie: 5 }, cooldownSimMin: 20 },
]

export function affordancesIn(loc: LocationId): Affordance[] {
    return AFFORDANCES.filter(a => a.location === loc)
}

export function affordanceById(id: string): Affordance | undefined {
    return AFFORDANCES.find(a => a.id === id)
}

/** Best prop to raise `mood`: any with a positive delta for it, preferring the current room, then the biggest delta. */
export function bestAffordanceFor(mood: MoodKey, from?: LocationId): Affordance | null {
    const candidates = AFFORDANCES.filter(a => (a.effects[mood] ?? 0) > 0)
    if (candidates.length === 0) return null
    const here = from ? candidates.filter(a => a.location === from) : []
    const pool = here.length ? here : candidates
    return pool.slice().sort((a, b) => (b.effects[mood] ?? 0) - (a.effects[mood] ?? 0))[0]
}
